// Helper canônico de fuso da Skynet. TODA lógica de "que dia/hora é em BRT"
// passa por aqui — em vez de espalhar `setUTCHours(3)`, `getUTCHours() < 3`,
// `T03:00:00.000Z` e `- 3 * 3600 * 1000` por dezenas de crons (cada um com um
// bug sutil de virada de dia).
//
// Os crons do Vercel SEMPRE disparam em UTC (não dá pra mudar). O que muda é:
// o código nunca mais raciocina em UTC — pede dia/hora/janela em BRT a este
// módulo. O offset é derivado de `America/Sao_Paulo` via Intl, então continua
// correto se o horário de verão voltar (hoje o Brasil é UTC-3 fixo desde 2019).

export const TZ = "America/Sao_Paulo";

const dateTimeFmtBRT = new Intl.DateTimeFormat("pt-BR", {
  timeZone: TZ,
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

/** "27/09/2026 17:00" em BRT, independente do fuso do processo chamador. */
export function dataHoraBRT(iso: string): string {
  const partes: Record<string, string> = {};
  for (const p of dateTimeFmtBRT.formatToParts(new Date(iso))) partes[p.type] = p.value;
  return `${partes.day}/${partes.month}/${partes.year} ${partes.hour}:${partes.minute}`;
}

const partsFmt = new Intl.DateTimeFormat("en-US", {
  timeZone: TZ,
  hour12: false,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

interface BrtParts {
  ano: number;
  mes: number; // 1-12
  dia: number;
  hora: number; // 0-23
  min: number;
  seg: number;
}

function partsBRT(instant: Date): BrtParts {
  const p = partsFmt.formatToParts(instant).reduce<Record<string, string>>((acc, x) => {
    if (x.type !== "literal") acc[x.type] = x.value;
    return acc;
  }, {});
  return {
    ano: Number(p.year),
    mes: Number(p.month),
    dia: Number(p.day),
    hora: Number(p.hour) % 24, // Intl às vezes devolve "24" à meia-noite
    min: Number(p.minute),
    seg: Number(p.second),
  };
}

/** Offset (BRT − UTC) em ms no instante dado. Tipicamente −10_800_000 (−3h). */
function offsetMs(instant: Date): number {
  const b = partsBRT(instant);
  const comoUtc = Date.UTC(b.ano, b.mes - 1, b.dia, b.hora, b.min, b.seg);
  // arredonda pro segundo pra evitar ruído de ms
  return comoUtc - Math.floor(instant.getTime() / 1000) * 1000;
}

/** Instante UTC que, exibido em BRT, é `ano-mes-dia 00:00:00`. */
function inicioDoDiaUtc(ano: number, mes1: number, dia: number): Date {
  const meiaNoiteUtc = Date.UTC(ano, mes1 - 1, dia);
  // offset do dia (aprox. via meia-noite UTC desse dia — estável p/ BR sem DST)
  const off = offsetMs(new Date(meiaNoiteUtc));
  return new Date(meiaNoiteUtc - off);
}

/** "YYYY-MM-DD" do dia BRT de um instante (default: agora). */
export function diaBRT(d: Date | string | number = new Date()): string {
  const b = partsBRT(new Date(d));
  return `${b.ano}-${String(b.mes).padStart(2, "0")}-${String(b.dia).padStart(2, "0")}`;
}

/**
 * Soma dias de calendário a uma data `YYYY-MM-DD` já expressa em BRT.
 * A aritmética UTC fica encapsulada aqui só para normalizar mês/ano, sem
 * converter o instante nem depender do fuso do processo chamador.
 */
export function adicionarDiasBRT(data: string, dias: number): string {
  if (!Number.isSafeInteger(dias)) {
    throw new Error("adicionarDiasBRT: dias deve ser inteiro seguro");
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(data);
  if (!match) throw new Error(`adicionarDiasBRT: data BRT inválida (${data})`);

  const ano = Number(match[1]);
  const mes = Number(match[2]);
  const dia = Number(match[3]);
  const calendario = new Date(0);
  calendario.setUTCHours(0, 0, 0, 0);
  calendario.setUTCFullYear(ano, mes - 1, dia);
  if (
    calendario.getUTCFullYear() !== ano ||
    calendario.getUTCMonth() !== mes - 1 ||
    calendario.getUTCDate() !== dia
  ) {
    throw new Error(`adicionarDiasBRT: data BRT inválida (${data})`);
  }

  calendario.setUTCDate(calendario.getUTCDate() + dias);
  if (Number.isNaN(calendario.getTime())) {
    throw new Error("adicionarDiasBRT: deslocamento fora do intervalo");
  }
  const anoFinal = calendario.getUTCFullYear();
  if (anoFinal < 0 || anoFinal > 9999) {
    throw new Error("adicionarDiasBRT: deslocamento fora do intervalo");
  }
  return `${String(anoFinal).padStart(4, "0")}-${String(
    calendario.getUTCMonth() + 1,
  ).padStart(2, "0")}-${String(calendario.getUTCDate()).padStart(2, "0")}`;
}

/** "YYYY-MM-DD" do dia BRT corrente. */
export function hojeBRT(): string {
  return diaBRT(new Date());
}

/** Hora BRT (0-23) de um instante (default: agora). */
export function horaBRT(d: Date | string | number = new Date()): number {
  return partsBRT(new Date(d)).hora;
}

/** Minutos desde 00:00 BRT (0-1439) de um instante (default: agora). Para janelas "HH:MM-HH:MM". */
export function minutoDoDiaBRT(d: Date | string | number = new Date()): number {
  const { hora, min } = partsBRT(new Date(d));
  return hora * 60 + min;
}

/**
 * Instante UTC de um "YYYY-MM-DD[ HH:mm[:ss]]" (ou com "T") SEM timezone,
 * interpretando os campos como hora BRT. Date-only = 00:00 BRT. Retorna null
 * quando o formato não casa (ex.: string com Z/offset explícito — essas o
 * parse nativo já trata certo).
 *
 * Motivação: `new Date("2026-07-28")` é meia-noite UTC (= 21:00 BRT do dia
 * 27!) e `new Date("2026-07-28 08:00")` usa a TZ do processo (UTC na Vercel).
 */
export function instanteBRT(quando: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/.exec(quando.trim());
  if (!m) return null;
  const comoUtc = Date.UTC(
    Number(m[1]),
    Number(m[2]) - 1,
    Number(m[3]),
    Number(m[4] ?? 0),
    Number(m[5] ?? 0),
    Number(m[6] ?? 0),
  );
  return new Date(comoUtc - offsetMs(new Date(comoUtc)));
}

/**
 * Janela [00:00 BRT, 00:00 BRT do dia seguinte) em ISO UTC.
 * @param data "YYYY-MM-DD" em BRT; sem argumento usa o dia BRT corrente.
 */
export function janelaDiaBRT(data?: string): { inicio: string; fim: string } {
  const dia = data ?? hojeBRT();
  const [ano, mes, d] = dia.split("-").map(Number);
  const inicio = inicioDoDiaUtc(ano, mes, d);
  const fim = inicioDoDiaUtc(ano, mes, d + 1); // JS normaliza dia+1 (vira mês/ano)
  return { inicio: inicio.toISOString(), fim: fim.toISOString() };
}

/** Janela [1º dia 00:00 BRT, 1º do mês seguinte) em ISO UTC. mes é 1-12. */
export function janelaMesBRT(ano: number, mes1: number): { inicio: string; fim: string } {
  const inicio = inicioDoDiaUtc(ano, mes1, 1);
  const fim = inicioDoDiaUtc(mes1 === 12 ? ano + 1 : ano, mes1 === 12 ? 1 : mes1 + 1, 1);
  return { inicio: inicio.toISOString(), fim: fim.toISOString() };
}

/** Janela [segunda 00:00 BRT, segunda seguinte) da semana BRT que contém `ref`. */
export function janelaSemanaBRT(ref: Date = new Date()): { inicio: string; fim: string } {
  const [ano, mes, dia] = diaBRT(ref).split("-").map(Number);
  const dow = new Date(Date.UTC(ano, mes - 1, dia)).getUTCDay(); // 0=domingo
  const diasDesdeSegunda = (dow + 6) % 7; // segunda-feira como início da semana
  const segundaUtc = new Date(Date.UTC(ano, mes - 1, dia - diasDesdeSegunda));
  const seguinteUtc = new Date(Date.UTC(ano, mes - 1, dia - diasDesdeSegunda + 7));
  const fmt = (d: Date) =>
    `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
  return {
    inicio: janelaDiaBRT(fmt(segundaUtc)).inicio,
    fim: janelaDiaBRT(fmt(seguinteUtc)).inicio,
  };
}

/** Janela [1/jan 00:00 BRT, 1/jan do ano seguinte) em ISO UTC. */
export function janelaAnoBRT(ano: number): { inicio: string; fim: string } {
  return { inicio: inicioDoDiaUtc(ano, 1, 1).toISOString(), fim: inicioDoDiaUtc(ano + 1, 1, 1).toISOString() };
}

/** {ano, mes} (mes 1-12) do mês anterior ao mês BRT corrente. */
export function mesAnteriorBRT(ref: Date = new Date()): { ano: number; mes: number } {
  const b = partsBRT(ref);
  return b.mes === 1 ? { ano: b.ano - 1, mes: 12 } : { ano: b.ano, mes: b.mes - 1 };
}

/** Ano anterior ao ano BRT corrente. */
export function anoAnteriorBRT(ref: Date = new Date()): number {
  return partsBRT(ref).ano - 1;
}

/**
 * Semana "YYYY-Www" do dia BRT. Mesma heurística usada pelos crons de
 * consciência (não é ISO-8601 estrito), mas calculada sobre o dia em BRT.
 */
export function semanaBRT(ref: Date = new Date()): string {
  const [ano, mes, dia] = diaBRT(ref).split("-").map(Number);
  const inicioAno = Date.UTC(ano, 0, 1);
  const diff = (Date.UTC(ano, mes - 1, dia) - inicioAno) / 86_400_000;
  const semana = Math.ceil((diff + new Date(inicioAno).getUTCDay() + 1) / 7);
  return `${ano}-W${String(semana).padStart(2, "0")}`;
}
